import React, { useEffect, useState } from 'react'
import { Droppable } from '@hello-pangea/dnd'
import { connect } from 'react-redux'
import { isEqual } from 'lodash'
import history from '../history'

import { createActionInSchedule } from '../actions'
import { getTheme } from '../utils/themes'
import { actionListsAreEqual } from '../utils'
import {
  unixNow,
  scheduleDisplayDate,
  getReadableScheduleDate,
  getScheduleDaySubheading,
} from '../utils/dates'

import {
  ScheduleDayContainer,
  ScheduleAddActionButton,
  DateHeading,
} from './ui/Schedule'
import NewAction from './NewAction'
import ActionsList from './ActionsList'
import EventsList from './EventsList'
import DeadlinesList from './DeadlinesList'
import ScheduleDayHeader from './ScheduleDayHeader'
import { Box } from 'mds-web-ui'

const propsAreEqual = (prevProps, props) => {
  if (prevProps.date !== props.date) {
    return false
  }
  if (prevProps.darkMode !== props.darkMode) {
    return false
  }
  if (prevProps.newActionInURL !== props.newActionInURL) {
    return false
  }
  const equalActions = actionListsAreEqual(prevProps.actions, props.actions)
  if (!equalActions) return false

  if (!isEqual(prevProps.events, props.events)) {
    return false
  }

  if (!isEqual(prevProps.deadlines, props.deadlines)) {
    return false
  }

  return true
}

const ScheduleDayInner = ({
  date,
  createActionInSchedule,
  darkMode,
  newActionInURL,
  newActionParameters,
  actions = [],
  events = [],
  deadlines = [],
}) => {
  const [defaultTitle, setDefaultTitle] = useState(null)
  const [newAction, setNewAction] = useState(false)
  const displayDate = scheduleDisplayDate(date)
  const [headerHeight, setHeight] = useState(0)

  useEffect(() => {
    // whether we have the current date as the new Action due date in the URL
    const newActionDefault =
      newActionInURL &&
      // no list set
      newActionParameters.list == null &&
      // match date - e.g. 20210510
      (newActionParameters.due === date ||
        // match word, e.g. today
        newActionParameters.due === displayDate.toLowerCase())
    // store in state so we can clear it after the Action is created
    if (newActionDefault) {
      setNewAction(true)
      setDefaultTitle(newActionParameters.title)
    }
  }, [newActionInURL, newActionParameters, date, displayDate])

  const [newActionAtTop, setNewActionAtTop] = useState(false)
  const [newActionUpdatedAt, setNewActionUpdatedAt] = useState(null)
  const defaultTheme = getTheme(null, darkMode)
  const isLater = date === 'later'

  return (
    <Droppable
      droppableId={JSON.stringify({ schedule: date })}
      type="action"
      isDropDisabled={isLater}
    >
      {(provided, snapshot) => (
        <ScheduleDayContainer
          isDraggingOver={snapshot.isDraggingOver}
          ref={provided.innerRef}
          paddingTop={headerHeight}
          aria-label={getReadableScheduleDate(date)}
          {...provided.droppableProps}
        >
          <NewAction
            color={defaultTheme.text}
            bg={defaultTheme.bg}
            onSubmit={(title) => {
              setNewAction(false)
              createActionInSchedule({ title, date, top: true })
              setNewActionUpdatedAt(unixNow())
              if (defaultTitle) {
                // change URL so on refresh it won't re-create same Action
                history.push('/')
              }
              setDefaultTitle(null)
              setNewAction(true)
            }}
            onCancel={() => {
              setNewAction(false)
            }}
            show={newAction & newActionAtTop}
            key={`t${newActionUpdatedAt}`}
          />

          <ScheduleDayHeader setHeight={setHeight}>
            <DateHeading
              heading={displayDate}
              subheading={getScheduleDaySubheading(date)}
              darkMode={darkMode}
              onClick={
                isLater
                  ? // can't create Actions in Later
                    null
                  : (e) => {
                      setNewActionAtTop(!e.shiftKey)
                      setNewAction(true)
                    }
              }
            />
            <EventsList events={events} date={date} />
            <DeadlinesList deadlines={deadlines} date={date} />
            {events.length || deadlines.length ? <Box mb={3} /> : null}
          </ScheduleDayHeader>

          <ActionsList
            color={defaultTheme.text}
            actions={actions}
            scheduleDay={date}
            inSchedule
            showEmptyListMessage={!snapshot.isDraggingOver && !newAction}
          />

          <NewAction
            color={defaultTheme.text}
            bg={defaultTheme.bg}
            onSubmit={(title) => {
              setNewAction(false)
              createActionInSchedule({ title, date, top: false })
              setNewActionUpdatedAt(unixNow())
              if (defaultTitle) {
                // change URL so on refresh it won't re-create same Action
                history.push('/')
              }
              setDefaultTitle(null)
              setNewAction(true)
            }}
            onCancel={() => {
              setNewAction(false)
            }}
            show={newAction & !newActionAtTop}
            key={`b${newActionUpdatedAt}`}
            defaultTitle={defaultTitle}
          />

          {isLater ? null : (
            <>
              {provided.placeholder}
              <ScheduleAddActionButton
                darkMode={darkMode}
                date={displayDate}
                newActionIsVisible={newAction}
                onClick={(e) => {
                  setNewActionAtTop(e.shiftKey)
                  setNewAction(true)
                }}
              />
            </>
          )}
        </ScheduleDayContainer>
      )}
    </Droppable>
  )
}

ScheduleDayInner.displayName = 'ScheduleDayInner'

const mapStateToProps = (state) => ({
  newActionInURL: state.location.newAction,
  newActionParameters: state.location.newActionParameters,
})

const ScheduleDay = connect(mapStateToProps, { createActionInSchedule })(
  React.memo(ScheduleDayInner, propsAreEqual),
)

ScheduleDay.displayName = 'ScheduleDay'

export default ScheduleDay
